import { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";
import { Instagram, Youtube, Facebook, Twitter, FileText, Plus, X, Trash2, ExternalLink, Check, Upload, Eye, Edit3, Heart, MessageCircle, PlaySquare, BarChart3 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { sortByUpcoming, fmtDate } from "../lib/formatters";
import { useAuth } from "../lib/auth";

const PLATFORMS = [
  { key: "Instagram", icon: Instagram, color: "#E4405F" },
  { key: "YouTube", icon: Youtube, color: "#FF0000" },
  { key: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "Twitter", icon: Twitter, color: "#1DA1F2" },
  { key: "Blog", icon: FileText, color: "#1B7A3E" },
];
const STATUSES = ["draft", "scheduled", "published"];
const STATUS_PILL = { draft: "pill-gray", scheduled: "pill-amber", published: "pill-green" };

// ------------------------------------------------------------
// Engagement scoring — used to colour-code cards on the calendar so
// wins pop visually. Score = likes + 2×comments + 0.5×views (comments
// are worth twice a like because they represent deeper engagement).
// Thresholds are absolute per-post, tuned for a small SMB audience;
// tweak if the community grows beyond ~10k reach/post.
// ------------------------------------------------------------
const HIGH_SCORE = 250;
const MED_SCORE  = 60;
const engagementScore = (r) => {
  const l = Number(r.likes || 0);
  const c = Number(r.comments || 0);
  const v = Number(r.views || 0);
  return l + (c * 2) + (v * 0.5);
};
const engagementTier = (r) => {
  if (r.status !== "published") return "none";
  const s = engagementScore(r);
  if (s <= 0) return "none";
  if (s >= HIGH_SCORE) return "high";
  if (s >= MED_SCORE) return "med";
  return "low";
};
const TIER_BORDER = {
  high: "border-l-4 border-l-emerald-500 border-emerald-300",
  med:  "border-l-4 border-l-amber-500 border-amber-300",
  low:  "border-l-4 border-l-slate-300 border-[color:var(--aii-border)]",
  none: "border-[color:var(--aii-border)]",
};

// ------------------------------------------------------------
// PostCard — one calendar cell entry. Shows title + platform icon
// + status pill by default. When `showStats` is on and the post has
// any engagement, a compact stats strip (likes / comments / views)
// appears under the meta row. Left border is colour-coded by
// engagementTier so high performers pop even when stats are hidden.
// ------------------------------------------------------------
function PostCard({ r, showStats, onEdit }) {
  const P = PLATFORMS.find(x => x.key === r.platform);
  const Icon = P?.icon || FileText;
  const tier = engagementTier(r);
  const hasStats = Number(r.likes || 0) + Number(r.comments || 0) + Number(r.views || 0) > 0;
  return (
    <div data-testid={`post-card-${r.id}`}
         data-tier={tier}
         onClick={() => onEdit(r)}
         className={`rounded-md p-2 hover:border-brand cursor-pointer border ${TIER_BORDER[tier]}`}>
      <div className="flex items-center gap-1.5 text-xs">
        <Icon size={11} style={{ color: P?.color }}/>
        <span className="font-semibold truncate flex-1">{r.title}</span>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-[color:var(--aii-muted)]">{r.scheduled_time || "no time"}</span>
        <span className={`pill ${STATUS_PILL[r.status] || 'pill-gray'}`}>{r.status}</span>
      </div>
      {showStats && hasStats && (
        <div className="mt-1.5 pt-1.5 border-t border-dashed border-[color:var(--aii-border)] flex items-center gap-2 text-[10px] text-[color:var(--aii-text-2)]"
             data-testid={`post-stats-${r.id}`}>
          {r.likes ? <span className="inline-flex items-center gap-0.5"><Heart size={9} className="text-rose-500"/>{r.likes.toLocaleString()}</span> : null}
          {r.comments ? <span className="inline-flex items-center gap-0.5"><MessageCircle size={9} className="text-sky-600"/>{r.comments.toLocaleString()}</span> : null}
          {r.views ? <span className="inline-flex items-center gap-0.5"><PlaySquare size={9} className="text-violet-600"/>{r.views.toLocaleString()}</span> : null}
          {tier === "high" && <span className="ml-auto text-[9px] font-bold text-emerald-700 uppercase tracking-widest">🔥 Win</span>}
        </div>
      )}
    </div>
  );
}

// Platform stat tiles — extracted from the main Content() to keep it lean.
// Renders 5 platform cards with total + published counts derived from `rows`.
function PlatformTiles({ rows }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {PLATFORMS.map((p) => {
        const total = rows.filter((r) => r.platform === p.key).length;
        const published = rows.filter((r) => r.platform === p.key && r.status === "published").length;
        const Icon = p.icon;
        return (
          <div key={p.key} className="aii-card p-4">
            <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: p.color }}/><span className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)]">{p.key}</span></div>
            <div className="text-2xl font-display font-semibold">{total}</div>
            <div className="text-xs text-[color:var(--aii-muted)]">{published} published</div>
          </div>
        );
      })}
    </div>
  );
}

const emptyForm = () => ({
  title: "", platform: "Instagram", scheduled_date: "", scheduled_time: "",
  status: "draft", caption: "", tags: "", linked_activity_id: "", linked_program_id: "", linked_partner_id: "",
  media_url: "", media_file_id: "", notes: "",
  likes: 0, comments: 0, views: 0,
});

export default function Content() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [view, setView] = useState("week"); // week | list
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week starting today
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Engagement stats toggle — Founder/SMCM can hide the stat strip when
  // the calendar starts to feel cluttered. Persisted to localStorage so
  // the preference sticks across sessions.
  const [showStats, setShowStats] = useState(() => {
    try { const v = window.localStorage.getItem("aii.content.showStats"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  useEffect(() => { try { window.localStorage.setItem("aii.content.showStats", showStats ? "1" : "0"); } catch { /* private mode */ } }, [showStats]);
  const fileInputRef = useRef(null);

  const load = async () => {
    const [c, a, p, pa] = await Promise.all([
      api.get("/content"),
      api.get("/activities").catch(() => ({ data: [] })),
      api.get("/program-bookings").catch(() => ({ data: [] })),
      api.get("/partner-accounts").catch(() => ({ data: [] })),
    ]);
    setRows(sortByUpcoming(c.data, "scheduled_date"));
    setActivities(a.data); setPrograms(p.data); setPartners(pa.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    if (editing) {
      await api.patch(`/content/${editing}`, form);
      toast.success("Updated");
    } else {
      await api.post("/content", form);
      toast.success("Content added");
    }
    setOpen(false); setEditing(null); setForm(emptyForm()); await load();
  };
  const del = async (id) => { await api.delete(`/content/${id}`); await load(); };
  const setStatus = async (id, status) => {
    await api.patch(`/content/${id}`, { status });
    if (status === "published") toast.success("Published — Founder notified");
    await load();
  };
  const editIt = (r) => { setForm({ ...emptyForm(), ...r }); setEditing(r.id); setOpen(true); setPreview(false); };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Max 25MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = getToken();
      const url = `${API}/files/${data.id}?auth=${token}`;
      setForm(v => ({ ...v, media_url: url, media_file_id: data.id }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Week view: 7 days starting from today + weekOffset*7
  const startBase = new Date();
  startBase.setDate(startBase.getDate() + weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startBase); d.setDate(startBase.getDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const byDay = Object.fromEntries(weekDays.map(d => [d, []]));
  const undated = [];
  const outOfRange = [];
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  for (const r of rows) {
    if (!r.scheduled_date) undated.push(r);
    else if (byDay[r.scheduled_date]) byDay[r.scheduled_date].push(r);
    else outOfRange.push(r);
  }

  const canManageContent = user.effective_role === "SMCM" || user.effective_role === "FOUNDER";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Social Media & Content</div>
          <h1 className="text-2xl font-semibold">Content Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="stats-toggle"
            onClick={() => setShowStats(v => !v)}
            title={showStats ? "Hide engagement stats" : "Show engagement stats"}
            className={`text-xs px-2.5 py-1.5 rounded border font-semibold flex items-center gap-1 ${showStats ? "bg-brand text-white border-brand" : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
            <BarChart3 size={12}/> Stats {showStats ? "on" : "off"}
          </button>
          <div className="bg-white border border-[color:var(--aii-border)] rounded-md p-0.5 flex">
            {["week","list"].map(v => (
              <button key={v} onClick={()=>setView(v)}
                className={`px-3 py-1 text-xs font-semibold rounded ${view===v ? "bg-brand text-white" : "text-[color:var(--aii-text-2)]"}`}>
                {v === "week" ? "Week" : "All"}
              </button>
            ))}
          </div>
          {canManageContent && (
            <button data-testid="content-new" onClick={()=>{ setEditing(null); setForm(emptyForm()); setOpen(true); }}
              className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
              <Plus size={14}/> New post
            </button>
          )}
        </div>
      </div>

      {/* Platform tiles */}
      <PlatformTiles rows={rows}/>

      {view === "week" ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button data-testid="week-prev" onClick={()=>setWeekOffset(o=>o-1)} className="p-1.5 rounded border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]" title="Previous week">‹</button>
              <button data-testid="week-today" onClick={()=>setWeekOffset(0)} className={`px-3 py-1 rounded text-xs font-semibold border ${weekOffset===0 ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>Today</button>
              <button data-testid="week-next" onClick={()=>setWeekOffset(o=>o+1)} className="p-1.5 rounded border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]" title="Next week">›</button>
            </div>
            <div className="text-sm text-[color:var(--aii-text-2)]">
              {fmtDate(weekStart)} — {fmtDate(weekEnd)}
              {outOfRange.length > 0 && <span className="ml-3 text-xs text-[color:var(--aii-muted)]">· {outOfRange.length} outside this week</span>}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-8 gap-3">
            {weekDays.map(d => (
              <div key={d} className="aii-card p-3 min-h-40">
                <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold">{new Date(d).toLocaleDateString(undefined,{weekday:'short'})}</div>
                <div className="text-lg font-display font-semibold">{new Date(d).getDate()}</div>
                <div className="mt-2 space-y-2">
                  {byDay[d].length === 0 && <div className="text-xs text-[color:var(--aii-muted)] italic">—</div>}
                  {byDay[d].map(r => <PostCard key={r.id} r={r} showStats={showStats} onEdit={editIt}/>)}
                </div>
              </div>
            ))}
            {/* Undated column */}
            <div className="aii-card p-3 min-h-40 border-dashed" data-testid="undated-col">
              <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold">Undated</div>
              <div className="text-lg font-display font-semibold">{undated.length}</div>
              <div className="mt-2 space-y-2">
                {undated.length === 0 && <div className="text-xs text-[color:var(--aii-muted)] italic">—</div>}
                {undated.map(r => <PostCard key={r.id} r={r} showStats={showStats} onEdit={editIt}/>)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="aii-card overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="aii-table">
              <thead><tr><th>Date</th><th>Time</th><th>Platform</th><th>Title</th><th>Destination</th><th>City</th><th>Status</th><th>Tags</th><th></th></tr></thead>
              <tbody>
                {rows.length===0 && <tr><td colSpan={9} className="text-center py-10 text-[color:var(--aii-muted)]">Nothing yet.</td></tr>}
                {rows.map(r=>{
                  const P = PLATFORMS.find(x=>x.key===r.platform); const Icon = P?.icon || FileText;
                  // Pull destination/city from the linked program booking so
                  // content rows carry the same location context Founder /
                  // GEHM see everywhere else.
                  const linkedProg = r.linked_program_id ? programs.find(p => p.id === r.linked_program_id) : null;
                  const dest = linkedProg?.destination || "";
                  const city = linkedProg?.city || "";
                  return (
                    <tr key={r.id}>
                      <td>{r.scheduled_date||"—"}</td>
                      <td>{r.scheduled_time||"—"}</td>
                      <td><span className="inline-flex items-center gap-1.5"><Icon size={13} style={{color:P?.color}}/> {r.platform}</span></td>
                      <td className="font-medium cursor-pointer hover:text-brand" onClick={()=>editIt(r)}>{r.title}</td>
                      <td className="text-xs whitespace-nowrap" data-testid={`content-dest-${r.id}`}>{dest || "—"}</td>
                      <td className="text-xs whitespace-nowrap" data-testid={`content-city-${r.id}`}>{city || "—"}</td>
                      <td>
                        {canManageContent ? (
                          <select value={r.status} onChange={e=>setStatus(r.id, e.target.value)} className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-xs">
                            {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <span className={`pill ${STATUS_PILL[r.status]||'pill-gray'}`}>{r.status}</span>
                        )}
                      </td>
                      <td className="text-xs text-[color:var(--aii-muted)]">{r.tags||"—"}</td>
                      <td className="text-right">
                        <div className="inline-flex gap-2 items-center">
                          {r.media_url && <a href={r.media_url} target="_blank" rel="noreferrer" className="text-brand"><ExternalLink size={13}/></a>}
                          {canManageContent && r.status !== 'published' && <button onClick={()=>setStatus(r.id, 'published')} className="text-xs bg-brand text-white px-2 py-1 rounded flex items-center gap-1"><Check size={11}/> Publish</button>}
                          {canManageContent && <button onClick={()=>del(r.id)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">{editing ? "Edit post" : "Plan a new post"}</h3>
                <button onClick={()=>{ setOpen(false); setEditing(null); }}><X size={18}/></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Title"><input data-testid="content-title" value={form.title} onChange={e=>setForm(v=>({...v,title:e.target.value}))} className="input-aii"/></Field>
                <Field label="Platform">
                  <select value={form.platform} onChange={e=>setForm(v=>({...v,platform:e.target.value}))} className="input-aii">
                    {PLATFORMS.map(p=><option key={p.key}>{p.key}</option>)}
                  </select>
                </Field>
                <Field label="Scheduled date"><input type="date" value={form.scheduled_date} onChange={e=>setForm(v=>({...v,scheduled_date:e.target.value}))} className="input-aii"/></Field>
                <Field label="Scheduled time"><input type="time" value={form.scheduled_time} onChange={e=>setForm(v=>({...v,scheduled_time:e.target.value}))} className="input-aii"/></Field>
                <Field label="Status">
                  <select value={form.status} onChange={e=>setForm(v=>({...v,status:e.target.value}))} className="input-aii">
                    {STATUSES.map(s=><option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Tags (comma sep.)"><input value={form.tags} onChange={e=>setForm(v=>({...v,tags:e.target.value}))} className="input-aii"/></Field>
                <Field label="Linked activity">
                  <select value={form.linked_activity_id} onChange={e=>setForm(v=>({...v,linked_activity_id:e.target.value}))} className="input-aii">
                    <option value="">—</option>
                    {activities.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </Field>
                <Field label="Linked program booking (tag participant)">
                  <select value={form.linked_program_id} onChange={e=>setForm(v=>({...v,linked_program_id:e.target.value}))} className="input-aii" data-testid="content-link-program">
                    <option value="">—</option>
                    {programs.map(p=><option key={p.id} value={p.id}>{p.customer} · {p.programme}</option>)}
                  </select>
                </Field>
                <Field label="Linked partner (visible in that Partner's portal)">
                  <select value={form.linked_partner_id} onChange={e=>setForm(v=>({...v,linked_partner_id:e.target.value}))} className="input-aii" data-testid="content-link-partner">
                    <option value="">—</option>
                    {(partners || []).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
              </div>

              {/* Media upload */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)] flex items-center justify-between">
                  <span>Media</span>
                  {form.media_url && <button onClick={()=>setForm(v=>({...v, media_url:"", media_file_id:""}))} className="text-xs text-red-600 hover:underline">Remove</button>}
                </label>
                {form.media_url ? (
                  <div className="mt-1 border border-[color:var(--aii-border)] rounded-md p-2 flex items-center gap-3 bg-[color:var(--aii-bg)]">
                    <img src={form.media_url} alt="preview" className="w-16 h-16 object-cover rounded-md border border-[color:var(--aii-border)]" onError={(e)=>{e.target.style.display='none';}}/>
                    <div className="flex-1 text-xs text-[color:var(--aii-text-2)] break-all">{form.media_url.slice(0,80)}…</div>
                    <a href={form.media_url} target="_blank" rel="noreferrer" className="text-brand"><ExternalLink size={14}/></a>
                  </div>
                ) : (
                  <div className="mt-1">
                    <input ref={fileInputRef} type="file" accept="image/*,video/mp4,.pdf" onChange={upload} className="hidden" data-testid="content-upload-input"/>
                    <button type="button" data-testid="content-upload-btn" onClick={()=>fileInputRef.current?.click()} disabled={uploading}
                      className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed border-[color:var(--aii-border)] rounded-md bg-white hover:bg-[color:var(--aii-green-tint)] hover:border-brand w-full justify-center">
                      <Upload size={14}/> {uploading ? "Uploading…" : "Upload image / video / PDF (max 25MB)"}
                    </button>
                  </div>
                )}
              </div>

              {/* Caption with markdown preview */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Caption (Markdown supported)</label>
                  <div className="bg-white border border-[color:var(--aii-border)] rounded p-0.5 flex text-xs">
                    <button type="button" onClick={()=>setPreview(false)} className={`px-2 py-0.5 rounded flex items-center gap-1 ${!preview ? "bg-brand text-white" : "text-[color:var(--aii-text-2)]"}`}><Edit3 size={11}/> Write</button>
                    <button type="button" onClick={()=>setPreview(true)} className={`px-2 py-0.5 rounded flex items-center gap-1 ${preview ? "bg-brand text-white" : "text-[color:var(--aii-text-2)]"}`}><Eye size={11}/> Preview</button>
                  </div>
                </div>
                {preview ? (
                  <div className="mt-1 min-h-[100px] p-3 border border-[color:var(--aii-border)] rounded-md bg-white text-sm prose prose-sm max-w-none">
                    {form.caption ? <ReactMarkdown>{form.caption}</ReactMarkdown> : <span className="text-[color:var(--aii-muted)] italic">Nothing to preview</span>}
                  </div>
                ) : (
                  <textarea data-testid="content-caption" rows={5} value={form.caption} onChange={e=>setForm(v=>({...v,caption:e.target.value}))}
                    className="input-aii font-mono text-sm" placeholder="Post copy…  Use **bold**, *italic*, [link](url), - lists"/>
                )}
              </div>

              <Field label="Notes"><textarea rows={2} value={form.notes} onChange={e=>setForm(v=>({...v,notes:e.target.value}))} className="input-aii"/></Field>
              <div className="flex justify-end gap-2">
                <button onClick={()=>{ setOpen(false); setEditing(null); }} className="px-4 py-2 text-sm">Cancel</button>
                <button data-testid="content-save" onClick={submit} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">{editing ? "Save changes" : "Add to calendar"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{label}</label>{children}</div>
);
